import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import {
  actions as editorActions,
  selectors as editorSelectors,
  actions,
  ResumeSectionId,
} from 'builder/modules/resumeEditor'
import { groupSectionsBy } from 'builder/components/SectionsList'
import { generateRandomId } from 'builder/utils/generateRandomId'
import { SortableSection } from 'builder/components/SectionsList/SortableSection'
import { SectionsListContainer } from 'builder/components/SectionsList/styles'
import { DeleteSectionFunc, RenameSectionFunc, SectionData } from 'builder/components/Section'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import {
  AddCardFunc,
  CardOperationFunc,
  IsCardOpenFunc,
  MoveCardFunc,
  MoveSubItemFunc,
  UpdateCardFunc,
} from 'builder/components/Card'

type Props = {
  isOpened?: boolean
  sections: SectionData[]
}

export const ExpandableSectionWrapper = (props: Props) => {
  const { sections, isOpened } = props
  const openedCard = useTypedSelector(editorSelectors.openedCard)
  const resume = useTypedSelector(editorSelectors.resume)
  const levels = useTypedSelector(state => state.generalEditor.levels)
  const dispatch = useDispatch()
  const { hideSkillLevel, locale = 'en' } = resume || {}

  const groups = groupSectionsBy(sections, 'sortable')
  const sectionsOrder = groups.flatMap(group =>
    group.sortable
      ? group.sections.map(section => (section.isCustom ? `custom:${section.id}` : section.id))
      : [],
  )

  const updateCard: UpdateCardFunc = useCallback(
    (sectionId, cardId, values, debounce, isCustom) => {
      dispatch(actions.updateCard({ sectionId, cardId, values, debounce, isCustom }))
    },
    [dispatch],
  )

  const deleteCard: CardOperationFunc = useCallback(
    (cardId, sectionId, isCustom = false) => {
      dispatch(actions.deleteCard({ sectionName: sectionId, cardId, isCustom }))
    },
    [dispatch],
  )

  const moveCard: MoveCardFunc = useCallback(
    payload => {
      dispatch(actions.moveCard(payload))
    },
    [dispatch],
  )

  const renameSection: RenameSectionFunc = useCallback(
    payload => {
      dispatch(actions.renameSection({ ...payload, debounce: true }))
    },
    [dispatch],
  )

  const deleteSection: DeleteSectionFunc = useCallback(
    (id, options) => {
      dispatch(actions.deleteSection({ id, options }))
    },
    [dispatch],
  )

  const handleCardAdd: AddCardFunc = useCallback(
    (sectionId, options) => {
      const cardId = generateRandomId()
      const { scrollIntoViewport = false } = options || {}
      dispatch(
        actions.addCard({
          sectionName: sectionId,
          cardId,
          options: { scrollIntoViewport, shouldOpen: true },
        }),
      )
    },
    [dispatch],
  )

  const isCardOpen: IsCardOpenFunc = useCallback(
    (cardId, sectionId) => {
      if (!openedCard) return false

      return openedCard.id === cardId && openedCard.sectionId === sectionId
    },
    [openedCard],
  )

  const handleCardToggle: CardOperationFunc = useCallback(
    (cardId, sectionId) => {
      const isOpen = isCardOpen(cardId, sectionId)
      dispatch(editorActions.openCard(isOpen ? null : { id: cardId, sectionId }))
    },
    [dispatch, isCardOpen],
  )

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) return

      const draggableId = active.id as ResumeSectionId
      const oldIndex = sectionsOrder.indexOf(active.id as string)
      const newIndex = sectionsOrder.indexOf(over.id as string)

      dispatch(
        actions.moveSection({
          offset: newIndex - oldIndex,
          sectionId: draggableId,
        }),
      )
    },
    [dispatch, sectionsOrder],
  )

  const addCardSubItem: CardOperationFunc = useCallback(
    (cardId, sectionId) => {
      dispatch(actions.addCardSubItem({ sectionName: sectionId, cardId }))
    },
    [dispatch],
  )

  const moveCardSubItem: MoveSubItemFunc = useCallback(
    payload => {
      dispatch(actions.moveCardSubItem(payload))
    },
    [dispatch],
  )

  return (
    <SectionsListContainer>
      {groups.map((group, groupIndex) => {
        const content = group.sections.map((section, sectionIndex) => (
          <SortableSection
            item={section}
            key={section.id}
            index={sectionIndex}
            isOpened={isOpened}
            levels={levels}
            hideSkillLevel={hideSkillLevel}
            locale={locale}
            onRename={renameSection}
            onDelete={deleteSection}
            onAdd={handleCardAdd}
            isCardOpen={isCardOpen}
            onCardChange={updateCard}
            onCardDelete={deleteCard}
            onCardMove={moveCard}
            onCardToggle={handleCardToggle}
            onAddCardSubItem={addCardSubItem}
            onSubItemMove={moveCardSubItem}
          />
        ))

        if (!group.sortable) return <div key={groupIndex}>{content}</div>

        return (
          <DndContext key={groupIndex} onDragEnd={onDragEnd} collisionDetection={closestCenter}>
            <SortableContext items={sectionsOrder} strategy={verticalListSortingStrategy}>
              {content}
            </SortableContext>
          </DndContext>
        )
      })}
    </SectionsListContainer>
  )
}
